import {
  useEffect, useState, useRef, useContext,
} from 'react';

import SearchIcon from 'assets/icons/search.svg';
import cn from 'classnames/bind';
import { useToggleState } from 'hooks';
import Link from 'next/link';
import { Button } from 'ui';
import SupportLink from 'ui/components/brainable/SupportLink';
import Logo from 'ui/components/Logo';
import { Context as LayoutContext } from 'ui/containers/LayoutContext';
import SearchPopup from 'ui/layouts/SearchPopup';

import s from './Header.module.scss';

const cx = cn.bind(s);

const Header = ({
  append,
  filled = false,
  short = false,
  hasLoginAction = true,
  hasSearchSuggestions = true,
  mobileNavigation,
  mainNavigation,
  authMenu,
  isAuth,
  logoText,
}) => {
  const { headerRef, setHeaderState } = useContext(LayoutContext);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isAuthVisible, setIsAuthVisible] = useState(!hasLoginAction);

  useEffect(() => {
    setIsAuthVisible(true);
  }, []);
  const {
    isActive: isSearchPopupOpen,
    open: openSearchPopup,
    close: closeSearchPopup,
  } = useToggleState(false);
  const lastScroll = useRef(0);
  const contentRef = useRef();
  useEffect(() => {
    const onScroll = () => {
      if (!filled) {
        setHasScrolled(window.scrollY > 0);
      }
      const isScrolledTop = lastScroll.current >= window.scrollY;
      setIsVisible(isScrolledTop || !append);
      lastScroll.current = window.scrollY;
    };
    if (!filled) {
      setHasScrolled(window.scrollY > 0);
    }

    lastScroll.current = window.scrollY;
    if (append || !filled) {
      window.addEventListener('scroll', onScroll);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [append, filled]);

  useEffect(() => {
    if (setHeaderState) {
      setHeaderState({
        isVisible,
        possibleOffset: contentRef.current
          ? contentRef.current.offsetHeight
          : 0,
        isHidingEnabled: !!append,
      });
    }
  }, [setHeaderState, isVisible, append]);

  return (
    <>
      <div
        ref={headerRef}
        className={cx('wrapper', {
          hidden: !isVisible && !!append && !short,
          wrapperStatic: short,
        })}
      >
        <div
          className={cx('container', {
            containerFilled: hasScrolled || !!append || filled,
            containerShort: short,
          })}
        >
          <div
            className={cx({
              content: !short,
              short,
              'brainable-container': short,
            })}
          >
            <div ref={contentRef} className={cx('contentInner')}>
              <Link href="/">
                <Logo as="span" text={logoText} />
              </Link>
              {short ? (
                <div className={cx('supportContent')}>
                  <SupportLink />
                </div>
              ) : (
                <div className={cx('headerContent')}>
                  <div className={cx('navContainer')}>{mainNavigation}</div>
                  <div
                    className={cx('headerTools', {
                      headerToolsOffset: isAuth,
                      headerAuthHidden: !isAuthVisible,
                    })}
                  >
                    <span className={cx('searchIcon')}>
                      <button
                        className={cx('searchButton')}
                        onClick={openSearchPopup}
                      >
                        <SearchIcon />
                      </button>
                    </span>
                    {authMenu || (
                      <div className={cx('headerNonAuthTools')}>
                        {hasLoginAction && (
                          <Link href={'/login'} className={cx('authLink')}>
                            Log in
                          </Link>
                        )}
                        <Button
                          variant="outlined"
                          href={'/free-personality-test'}
                          as="a"
                          size="small"
                        >
                          Take the test
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {mobileNavigation && (
                <div className={cx('mobileNavContainer')}>
                  {mobileNavigation}
                </div>
              )}
            </div>
          </div>
        </div>
        {append ? <div className={cx('append')}>{append}</div> : null}
      </div>
      <SearchPopup
        hasSuggestions={hasSearchSuggestions}
        isOpen={isSearchPopupOpen}
        onClose={closeSearchPopup}
      />
    </>
  );
};

export default Header;
